import { Outlet, Navigate, useLocation } from "react-router-dom";
import Sidebar from "@/components/Sidebar";
import Topbar from "@/components/Topbar";
import { useAuth } from "@/lib/auth";
import { SettingsProvider, useSettings } from "@/lib/settings";
import HaloWordmark from "@/components/HaloWordmark";
import MahanoTickMark from "@/components/MahanoTickMark";
import { TourProvider } from "@/lib/tour";
import ForcePasswordChange from "@/components/ForcePasswordChange";

// Routes a member can only open if the module is ticked for them.
const GATED_ROUTES = [
  ["/contacts", "contacts"], ["/projects", "projects"],
  ["/finance", "finance"], ["/calendar", "calendar"], ["/tasks", "tasks"],
  ["/documents", "documents"], ["/campaigns", "campaigns"], ["/news", "news"],
  ["/analytics", "analytics"], ["/data", "admin"],
];

function ModuleGate({ user, children }) {
  const location = useLocation();
  if (user.role !== "member") return children;
  const match = GATED_ROUTES.find(([path]) => location.pathname.startsWith(path));
  if (!match) return children;
  const [, module] = match;
  const allowed = module !== "admin" && (user.allowed_modules || []).includes(module);
  return allowed ? children : <Navigate to="/" replace />;
}

function OnboardingGate({ children }) {
  const { settings, loading } = useSettings() || {};
  const location = useLocation();
  // Only redirect once settings have actually loaded. If the flag is falsy AND
  // the user is not already on /onboarding, push them there. We don't gate
  // /onboarding itself so the wizard can render.
  if (!loading && settings && settings.onboarded !== true && location.pathname !== "/onboarding") {
    return <Navigate to="/onboarding" replace />;
  }
  return children;
}

export default function AppLayout({ onboarding = false }) {
  const { user, loading, refresh, logout } = useAuth();

  if (loading) {
    return (
      <div data-testid="loading-screen" className="min-h-screen flex flex-col items-center justify-center bg-background text-muted-foreground gap-4">
        <MahanoTickMark size={64} />
        <div className="text-sm flex items-center gap-1.5"><HaloWordmark height={12}/> is starting…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/welcome" replace />;

  // Invited employees land on a temporary password and must replace it first.
  if (user.must_change_password) {
    return <ForcePasswordChange user={user} onDone={refresh} onSignOut={logout} />;
  }

  // /onboarding renders without the sidebar/topbar chrome and without the gate.
  if (onboarding) {
    return (
      <SettingsProvider>
        <Outlet />
      </SettingsProvider>
    );
  }

  return (
    <SettingsProvider>
      <OnboardingGate>
        <TourProvider>
          <div className="min-h-screen flex bg-background">
            <Sidebar />
            <div className="flex-1 flex flex-col min-w-0">
              <Topbar />
              <main data-testid="main-content" className="flex-1 overflow-y-auto">
                <div className="max-w-[1400px] mx-auto px-4 md:px-8 lg:px-12 py-6 md:py-8">
                  <ModuleGate user={user}><Outlet /></ModuleGate>
                </div>
              </main>
            </div>
          </div>
        </TourProvider>
      </OnboardingGate>
    </SettingsProvider>
  );
}
